import React, { useMemo, useState } from 'react';
import {
    Box, Button, Grid, LinearProgress, Menu, MenuItem, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Typography,
} from '@mui/material';
import withLayout from '../../hocs/Layout';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import BarChartIcon from '@mui/icons-material/BarChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import PageTitle from '../../common/PageTitle';
import PageHero from '../../common/PageHero';
import KpiCard from './shared/KpiCard';
import FilterBar from './shared/FilterBar';
import { getDefaultDateRange, CLOSURE_CATEGORIES } from './shared/constants';
import useAppointmentDashboard from './shared/useAppointmentDashboard';
import { exportXlsx } from './shared/exportHelpers';

const { PROZO, NON_PROZO } = CLOSURE_CATEGORIES;

const chartCardSx = { bgcolor: '#fff', borderRadius: '10px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', height: '100%' };
const chartH3Sx = { fontSize: '14px', fontWeight: 700, color: '#1a1a2e', mb: '4px' };
const chartSubSx = { fontSize: '11px', color: '#999', mb: '14px' };
const perfThSx = { py: '10px', px: '10px', fontSize: '11px', fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '0.3px', bgcolor: '#fafafa', borderBottom: '2px solid #e0e0e0' };
const perfTdSx = { py: '10px', px: '10px', fontSize: '12px', color: '#333', borderBottom: '1px solid #f0f0f0' };

function MissReasonBar({ row, max }) {
    const pct = max ? Math.round((row.count / max) * 100) : 0;
    const isProzo = row.category === PROZO;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Box sx={{ width: 200, textAlign: 'right', fontSize: '11px', color: '#555', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', flexShrink: 0 }}>
                {row.label || row.code}
            </Box>
            <Box sx={{ flex: 1, height: '22px', bgcolor: '#f0f0f0', borderRadius: '4px', overflow: 'hidden', position: 'relative' }}>
                <Box sx={{
                    width: `${pct}%`, height: '100%', minWidth: '30px', borderRadius: '4px',
                    background: isProzo ? 'linear-gradient(90deg, #e65100, #ff8f00)' : 'linear-gradient(90deg, #1565c0, #42a5f5)',
                    display: 'flex', alignItems: 'center', pl: '8px', color: '#fff', fontWeight: 700, fontSize: '10px',
                }}>
                    {row.count}
                </Box>
            </Box>
        </Box>
    );
}

function AttributionDonut({ prozo, nonProzo, topProzo, topNonProzo }) {
    const total = (prozo || 0) + (nonProzo || 0);
    const prozoPct = total ? Math.round((prozo / total) * 100) : 0;
    const nonPct = total ? 100 - prozoPct : 0;
    const circumference = 2 * Math.PI * 70;
    const prozoLen = (prozoPct / 100) * circumference;
    const nonLen = circumference - prozoLen;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '30px', pt: 2 }}>
            <svg width='180' height='180' viewBox='0 0 180 180'>
                <circle cx='90' cy='90' r='70' fill='none' stroke='#e0e0e0' strokeWidth='28' />
                <circle cx='90' cy='90' r='70' fill='none' stroke='#1565c0' strokeWidth='28' strokeDasharray={`${nonLen} ${prozoLen}`} transform='rotate(-90 90 90)' />
                <circle cx='90' cy='90' r='70' fill='none' stroke='#e65100' strokeWidth='28' strokeDasharray={`${prozoLen} ${nonLen}`} strokeDashoffset={`-${nonLen}`} transform='rotate(-90 90 90)' />
                <text x='90' y='85' textAnchor='middle' fontSize='22' fontWeight='800' fill='#333'>{total}</text>
                <text x='90' y='102' textAnchor='middle' fontSize='10' fill='#888'>Total Misses</text>
            </svg>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#1565c0', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '12px' }}><strong>Non-Prozo:</strong> {nonProzo} ({nonPct}%)</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#e65100', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '12px' }}><strong>Prozo:</strong> {prozo} ({prozoPct}%)</Typography>
                </Box>
                <Box sx={{ borderTop: '1px solid #eee', pt: '12px', mt: '4px' }}>
                    <Typography sx={{ fontSize: '11px', color: '#999' }}>Top Prozo reason:</Typography>
                    <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>{topProzo?.label || '—'}{topProzo?.count != null ? ` (${topProzo.count})` : ''}</Typography>
                    <Typography sx={{ fontSize: '11px', color: '#999', mt: 1 }}>Top Non-Prozo reason:</Typography>
                    <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>{topNonProzo?.label || '—'}{topNonProzo?.count != null ? ` (${topNonProzo.count})` : ''}</Typography>
                </Box>
            </Box>
        </Box>
    );
}

function ReportsAnalytics({ abd = true }) {
    const def = getDefaultDateRange();
    const [filters, setFilters] = useState({ fromDate: def.fromDate, toDate: def.toDate });
    const [menuAnchor, setMenuAnchor] = useState(null);

    const { data: dash, loading } = useAppointmentDashboard({
        filters,
        include: ['buckets', 'summary', 'perf', 'reasons'],
        groupBy: ['courier'],
        appointmentRequired: abd,
    });
    const summary = dash;
    const reasons = useMemo(() => dash?.reasons || [], [dash]);
    const courier = useMemo(() => dash?.perf?.courier || [], [dash]);

    const maxCount = reasons.reduce((m, r) => Math.max(m, r.count || 0), 0);
    const sortedReasons = useMemo(() => [...reasons].sort((a, b) => (b.count || 0) - (a.count || 0)).slice(0, 10), [reasons]);

    const attribution = useMemo(() => {
        const prozo = reasons.filter((r) => r.category === PROZO).reduce((s, r) => s + (r.count || 0), 0);
        const nonProzo = reasons.filter((r) => r.category === NON_PROZO).reduce((s, r) => s + (r.count || 0), 0);
        const topProzo = reasons.filter((r) => r.category === PROZO).sort((a, b) => (b.count || 0) - (a.count || 0))[0];
        const topNonProzo = reasons.filter((r) => r.category === NON_PROZO).sort((a, b) => (b.count || 0) - (a.count || 0))[0];
        return { prozo, nonProzo, topProzo, topNonProzo };
    }, [reasons]);

    const reasonsSheet = useMemo(() => ({
        name: 'Miss Reasons',
        rows: reasons.map((r) => ({ Code: r.code, Label: r.label || r.code, Category: r.category, Count: r.count || 0 })),
    }), [reasons]);
    const attributionSheet = useMemo(() => {
        const total = (attribution.prozo || 0) + (attribution.nonProzo || 0);
        return {
            name: 'Attribution',
            rows: [
                { Bucket: 'Prozo Attributable', Count: attribution.prozo || 0, Percent: total ? Math.round((attribution.prozo / total) * 100) : 0, TopReason: attribution.topProzo?.label || '' },
                { Bucket: 'Non-Prozo Attributable', Count: attribution.nonProzo || 0, Percent: total ? Math.round((attribution.nonProzo / total) * 100) : 0, TopReason: attribution.topNonProzo?.label || '' },
            ],
        };
    }, [attribution]);
    const courierSheet = useMemo(() => ({
        name: 'Courier Performance',
        rows: courier.map((c) => ({
            Courier: c.label,
            Total: c.total || 0,
            Delivered: c.delivered || 0,
            'Missed (Prozo)': c.missedProzo || 0,
            'Missed (Non-Prozo)': c.missedNonProzo || 0,
            'Delivery Rate %': c.deliveryRate || 0,
        })),
    }), [courier]);

    const downloadSingle = (sheet, baseName) => { exportXlsx(baseName, [sheet]); setMenuAnchor(null); };
    const downloadAll = () => { exportXlsx('ABD_Analytics_AllReports', [reasonsSheet, attributionSheet, courierSheet]); setMenuAnchor(null); };

    return (
        <div id='main-content' className='container-fluid'>
            <PageTitle>Reports &amp; Analytics</PageTitle>
            {/* <PageHero title='Reports & Analytics' subtitle='Appointment performance and miss attribution insights' sx={{ mb: 2.5 }} /> */}
            {loading && <LinearProgress sx={{ mb: 1 }} />}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                    <Typography component='h2' sx={{ fontSize: '16px', fontWeight: 700, color: '#1a1a2e', mb: '2px' }}>
                        Reports &amp; Analytics — How Are Your Appointments Performing?
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#888' }}>
                        Performance overview for the selected date range
                    </Typography>
                </Box>
                <Box>
                    <Button
                        variant='outlined' size='small'
                        startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                        onClick={(e) => setMenuAnchor(e.currentTarget)}
                        sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', padding: '7px 14px', color: '#1565c0', borderColor: '#1565c0', '&:hover': { borderColor: '#1565c0', bgcolor: '#e3f2fd' } }}
                    >Download Reports ▾</Button>
                    <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)} PaperProps={{ sx: { mt: 0.5, minWidth: 280, borderRadius: '8px' } }}>
                        <Box sx={{ px: '14px', py: '10px', borderBottom: '1px solid #f0f0f0', fontSize: '11px', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Download Analytics Reports
                        </Box>
                        <MenuItem onClick={() => downloadSingle(reasonsSheet, 'ABD_MissReasonBreakdown')} sx={{ px: '14px', py: '10px', gap: '10px' }}>
                            <BarChartIcon sx={{ fontSize: 16, color: '#1565c0' }} />
                            <Box>
                                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#333' }}>Miss Reason Breakdown</Typography>
                                <Typography sx={{ fontSize: '10px', color: '#999' }}>All miss reasons with counts and attribution</Typography>
                            </Box>
                        </MenuItem>
                        <MenuItem onClick={() => downloadSingle(attributionSheet, 'ABD_ProzoVsNonProzo')} sx={{ px: '14px', py: '10px', gap: '10px' }}>
                            <PieChartIcon sx={{ fontSize: 16, color: '#e65100' }} />
                            <Box>
                                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#333' }}>Prozo vs Non-Prozo Attribution</Typography>
                                <Typography sx={{ fontSize: '10px', color: '#999' }}>Attribution split with detailed breakdown</Typography>
                            </Box>
                        </MenuItem>
                        <MenuItem onClick={() => downloadSingle(courierSheet, 'ABD_CourierPerformance')} sx={{ px: '14px', py: '10px', gap: '10px' }}>
                            <TimelineIcon sx={{ fontSize: 16, color: '#2e7d32' }} />
                            <Box>
                                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#333' }}>Courier-wise Performance</Typography>
                                <Typography sx={{ fontSize: '10px', color: '#999' }}>Delivery rates, miss counts per courier</Typography>
                            </Box>
                        </MenuItem>
                        <Box sx={{ px: '14px', py: '10px', borderTop: '1px solid #f0f0f0' }}>
                            <Button fullWidth size='small' onClick={downloadAll}
                                sx={{ bgcolor: '#1565c0', color: '#fff', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', padding: '5px 12px', borderRadius: '4px', '&:hover': { bgcolor: '#0d47a1' } }}
                            >Download All Reports (Excel)</Button>
                        </Box>
                    </Menu>
                </Box>
            </Box>

            <FilterBar
                show={['courier', 'dateRange']}
                onApply={(f) => setFilters(f)}
                onReset={() => setFilters({ fromDate: def.fromDate, toDate: def.toDate })}
            />

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={3}>
                    <KpiCard color='blue' label='Total Appointments' value={summary.totalAppts} sub='Created in range' icon={<EventNoteIcon />} />
                </Grid>
                <Grid item xs={12} md={3}>
                    <KpiCard color='green' label='First-Attempt Delivery Rate' value={`${summary.firstAttemptRate ?? 0}%`} sub='Delivered on Appointment #1' icon={<CheckCircleIcon />} />
                </Grid>
                <Grid item xs={12} md={3}>
                    <KpiCard color='amber' label='Avg Attempts per Delivery' value={summary.avgAttempts ?? 0} sub='Across delivered shipments' icon={<TrendingUpIcon />} />
                </Grid>
                <Grid item xs={12} md={3}>
                    <KpiCard color='red' label='Prozo Attributable Miss Rate' value={`${summary.prozoMissRate ?? 0}%`} sub='Of all missed appointments' icon={<ReportProblemIcon />} />
                </Grid>
            </Grid>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '18px', mb: '18px' }}>
                <Box sx={chartCardSx}>
                    <Typography component='h3' sx={chartH3Sx}>Miss Reason Breakdown</Typography>
                    <Typography sx={chartSubSx}>Top reasons for missed appointments in selected range</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {sortedReasons.length === 0 && <Typography sx={{ fontSize: '11px', color: '#999' }}>No data</Typography>}
                        {sortedReasons.map((r) => (
                            <MissReasonBar key={r.code} row={r} max={maxCount} />
                        ))}
                    </Box>
                </Box>
                <Box sx={chartCardSx}>
                    <Typography component='h3' sx={chartH3Sx}>Prozo vs Non-Prozo Attribution</Typography>
                    <Typography sx={chartSubSx}>Who was responsible for missed appointments?</Typography>
                    <AttributionDonut prozo={attribution.prozo} nonProzo={attribution.nonProzo} topProzo={attribution.topProzo} topNonProzo={attribution.topNonProzo} />
                </Box>
            </Box>

            <Box sx={{ ...chartCardSx, mb: '18px' }}>
                <Typography component='h3' sx={chartH3Sx}>Courier-wise Appointment Performance</Typography>
                <Typography sx={chartSubSx}>Delivery success rate by courier partner</Typography>
                <TableContainer sx={{ mt: '12px' }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#fafafa' }}>
                                <TableCell sx={perfThSx}>Courier Partner</TableCell>
                                <TableCell sx={{ ...perfThSx, textAlign: 'center' }} align='center'>Total Appts</TableCell>
                                <TableCell sx={{ ...perfThSx, textAlign: 'center' }} align='center'>Delivered</TableCell>
                                <TableCell sx={{ ...perfThSx, textAlign: 'center' }} align='center'>Missed (Prozo)</TableCell>
                                <TableCell sx={{ ...perfThSx, textAlign: 'center' }} align='center'>Missed (Non-Prozo)</TableCell>
                                <TableCell sx={{ ...perfThSx, textAlign: 'center' }} align='center'>Delivery Rate</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {courier.length === 0 && (
                                <TableRow><TableCell colSpan={6} align='center' sx={{ color: '#888', py: 2, fontSize: '12px' }}>No data</TableCell></TableRow>
                            )}
                            {courier.map((c) => (
                                <TableRow key={c.key || c.label} hover>
                                    <TableCell sx={perfTdSx}>
                                        {c.label}
                                        {c.key && <Typography component='span' sx={{ fontSize: '11px', color: '#888', ml: 0.5 }}>({c.key})</Typography>}
                                    </TableCell>
                                    <TableCell sx={perfTdSx} align='center'>{c.total}</TableCell>
                                    <TableCell sx={perfTdSx} align='center'>{c.delivered}</TableCell>
                                    <TableCell sx={perfTdSx} align='center'>{c.missedProzo}</TableCell>
                                    <TableCell sx={perfTdSx} align='center'>{c.missedNonProzo}</TableCell>
                                    <TableCell sx={perfTdSx} align='center'>
                                        <Typography component='span' sx={{ fontWeight: 700, fontSize: '12px', color: (c.deliveryRate || 0) >= 70 ? '#2e7d32' : '#e65100' }}>
                                            {c.deliveryRate || 0}%
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </div>
    );
}

export default withLayout(ReportsAnalytics);
